import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Clock, AlertTriangle, TrendingUp, Users, ChevronUp, ChevronDown, ChevronsUpDown, Eye } from "lucide-react";
import { useState } from "react";
import SubTabs from "@/components/SubTabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DISPLAY_NAMES = { Koebenhavn: "København" };
const displayCity = (c) => DISPLAY_NAMES[c] || c;

const CITIES = ["Koebenhavn", "Aarhus", "Odense", "Aalborg", "Kolding"];

function fmtHours(h) {
  if (!h && h !== 0) return "—";
  return `${h.toLocaleString("da-DK", { minimumFractionDigits: 0, maximumFractionDigits: 1 })} t`;
}

function fmtPct(p) {
  if (!p && p !== 0) return "—";
  return `${p.toLocaleString("da-DK", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

function getAbsenceColor(pct) {
  if (pct >= 15) return "#EF4444";
  if (pct >= 10) return "#F97316";
  if (pct >= 5) return "#EAB308";
  return "#4ADE80";
}

const COLS = [
  { key: "start_date",    label: "Start" },
  { key: "expected_exam", label: "Slut" },
  { key: "arbejdstid",    label: "Praktiske t." },
  { key: "syg_frav",      label: "Syg/Frav" },
  { key: "kpi_fravær_pct", label: "Frav.%" },
  { key: "tilbage",       label: "Tilbage" },
];

function TypeBadge({ type }) {
  if (type === "kos") return <span className="text-[13px] font-bold ml-1" style={{ color: "#EC4899" }}>(K)</span>;
  if (type === "fri") return <span className="text-[13px] font-bold ml-1" style={{ color: "#3B82F6" }}>(F)</span>;
  return null;
}

function fmtYearMonth(ym) {
  if (!ym) return "—";
  if (ym === "Færdig") return "Færdig";
  const [y, m] = ym.split("-").map(Number);
  const months = ["jan", "feb", "mar", "apr", "maj", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
  return `${months[m - 1]} ${String(y).slice(-2)}`;
}

function fmtStartDate(d) {
  if (!d) return "—";
  try {
    const dt = new Date(d);
    const months = ["jan", "feb", "mar", "apr", "maj", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
    return `${months[dt.getMonth()]} ${String(dt.getFullYear()).slice(-2)}`;
  } catch { return "—"; }
}

function SortableTable({ students, onImpersonate }) {
  const [sortKey, setSortKey] = useState("arbejdstid");
  const [sortDir, setSortDir] = useState("desc");

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === "desc" ? "asc" : "desc");
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const sorted = [...students].sort((a, b) => {
    let valA, valB;
    if (sortKey === "tilbage") {
      valA = a.normeret_timer > 0 ? Math.max(0, a.normeret_timer - a.gennemfort_timer) : -1;
      valB = b.normeret_timer > 0 ? Math.max(0, b.normeret_timer - b.gennemfort_timer) : -1;
    } else if (sortKey === "start_date" || sortKey === "expected_exam") {
      valA = a[sortKey] || "";
      valB = b[sortKey] || "";
      return sortDir === "desc" ? valB.localeCompare(valA) : valA.localeCompare(valB);
    } else if (sortKey === "syg_frav") {
      valA = (a.sygdom ?? 0) + (a.fravær ?? 0);
      valB = (b.sygdom ?? 0) + (b.fravær ?? 0);
    } else {
      valA = a[sortKey] ?? 0;
      valB = b[sortKey] ?? 0;
    }
    return sortDir === "desc" ? valB - valA : valA - valB;
  });

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ChevronsUpDown className="w-3 h-3 opacity-30 inline ml-0.5" />;
    return sortDir === "desc"
      ? <ChevronDown className="w-3 h-3 inline ml-0.5 text-foreground" />
      : <ChevronUp className="w-3 h-3 inline ml-0.5 text-foreground" />;
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[13px]">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left py-2 pr-3 text-muted-foreground font-medium">Navn</th>
            {COLS.map(col => (
              <th
                key={col.key}
                className="text-right py-2 px-2 text-muted-foreground font-medium cursor-pointer select-none hover:text-foreground transition-colors"
                onClick={() => handleSort(col.key)}
              >
                {col.label}<SortIcon col={col.key} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((s, i) => {
            const kpiFravær = s.kpi_fravær_pct ?? s.fravær_pct;
            const absColor = getAbsenceColor(kpiFravær);
            const tilbage = s.normeret_timer > 0 ? Math.max(0, Math.round(s.normeret_timer - s.gennemfort_timer)) : null;
            return (
              <tr key={i} className="border-b border-border hover:bg-secondary/50 transition-colors">
                <td className="py-2 pr-3 font-medium truncate max-w-[160px]">
                  <span className="flex items-center">
                    {onImpersonate ? (
                      <button
                        onClick={() => onImpersonate(s.name)}
                        className="flex items-center gap-1 text-foreground hover:text-[#FF2A6D] transition-colors group"
                        title={`Se som ${s.name}`}
                      >
                        <span>{s.name}</span>
                        <Eye className="w-3 h-3 opacity-0 group-hover:opacity-60 transition-opacity shrink-0" />
                      </button>
                    ) : (
                      <span className="text-foreground">{s.name}</span>
                    )}
                    <TypeBadge type={s.uddannelse_type} />
                    {/* Opfølgning #33 (#5): elev færdig med timer, venter på eksamen */}
                    {s.beregnet_status === "Klar til eksamen" && (
                      <span className="ml-1.5 shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 text-[11px] font-semibold whitespace-nowrap">
                        Klar til eksamen{s.expected_exam ? ` ${fmtYearMonth(s.expected_exam)}` : ""}
                      </span>
                    )}
                  </span>
                </td>
                <td className="py-2 px-2 text-right text-muted-foreground text-[13px]">{fmtStartDate(s.start_date)}</td>
                <td className="py-2 px-2 text-right text-muted-foreground text-[13px]">{fmtYearMonth(s.expected_exam)}</td>
                <td className="py-2 px-2 text-right text-foreground">{fmtHours(s.arbejdstid)}</td>
                <td className="py-2 px-2 text-right" style={{ color: (s.sygdom + s.fravær) > 0 ? "#F97316" : "#6b7280" }}>{fmtHours((s.sygdom ?? 0) + (s.fravær ?? 0))}</td>
                <td className="py-2 px-2 text-right font-bold" style={{ color: absColor }}>{fmtPct(kpiFravær)}</td>
                <td className="py-2 pl-2 text-right text-muted-foreground">{tilbage !== null ? fmtHours(tilbage) : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function KosFriSplit({ kos, fri, total }) {
  if (total === 0) return <p className="text-2xl font-bold text-foreground font-kpi">0</p>;
  if (kos === 0 && fri === 0) return <p className="text-2xl font-bold text-foreground font-kpi">{total}</p>;
  return (
    <p className="text-xl font-bold font-kpi leading-tight">
      <span style={{ color: "#EC4899" }}>{kos}</span>
      <span className="text-muted-foreground text-sm mx-0.5">/</span>
      <span style={{ color: "#3B82F6" }}>{fri}</span>
    </p>
  );
}

function SummaryCard({ city, students, onImpersonate, pendingStudents, nextExamInfo, orlovInfo }) {
  const activeStudentList = students || [];
  const pendingList = pendingStudents || [];
  const totalInCity = activeStudentList.length + pendingList.length;

  if (totalInCity === 0) return null;

  const cityKosCount = activeStudentList.filter(x => x.uddannelse_type === "kos").length;
  const cityFriCount = activeStudentList.filter(x => x.uddannelse_type === "fri").length;
  const withTimer = activeStudentList.filter(x => x.total > 0);
  const totalTotals = withTimer.reduce((s, x) => s + x.total, 0);
  const activeFravær = withTimer.reduce((s, x) => s + x.fravær, 0);
  const avgFraværPct = totalTotals > 0 ? (activeFravær / totalTotals) * 100 : 0;

  // Pending split
  const pendingKos = pendingList.filter(x => x.uddannelse_type === "kos").length;
  const pendingFri = pendingList.filter(x => x.uddannelse_type === "fri").length;
  const pendingCount = pendingList.length;

  // Eksamen split
  const examCount = nextExamInfo ? nextExamInfo.count : 0;
  const examKos = nextExamInfo ? (nextExamInfo.kos || 0) : 0;
  const examFri = nextExamInfo ? (nextExamInfo.fri || 0) : 0;
  const examLabel = nextExamInfo ? fmtYearMonth(nextExamInfo.exam) : "";

  // Orlov
  const orlovData = orlovInfo || {};
  const orlovTotal = typeof orlovData === "number" ? orlovData : (orlovData.total || 0);
  const orlovKos = typeof orlovData === "object" ? (orlovData.kos || 0) : 0;
  const orlovFri = typeof orlovData === "object" ? (orlovData.fri || 0) : 0;

  return (
    <Card className="bg-card border-border shadow-none">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-[#FF2A6D]" />
            <CardTitle className="text-sm font-bold tracking-tight">{displayCity(city)}</CardTitle>
          </div>
          <span className="text-[13px] text-muted-foreground">{totalInCity} elever</span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
          <div className="bg-secondary rounded-xl p-3">
            <p className="text-[13px] text-muted-foreground mb-1">Til eksamen {examLabel}</p>
            <KosFriSplit kos={examKos} fri={examFri} total={examCount} />
          </div>
          {/* Opfølgning #33 (#3): samlet elev-tal primært, kos/frisør-split sekundært. */}
          <div className="bg-secondary rounded-xl p-3">
            <p className="text-[13px] text-muted-foreground mb-1">Elever</p>
            <p className="text-2xl font-bold font-kpi text-foreground leading-tight">{cityKosCount + cityFriCount}</p>
            <p className="text-[12px] mt-0.5">
              <span style={{ color: "#EC4899" }}>{cityKosCount} kos</span>
              <span className="text-muted-foreground mx-1">·</span>
              <span style={{ color: "#3B82F6" }}>{cityFriCount} frisør</span>
            </p>
          </div>
          <div className="bg-secondary rounded-xl p-3">
            <p className="text-[13px] text-muted-foreground mb-1">Gns. fraværspct.</p>
            <p className="text-2xl font-bold font-kpi" style={{ color: getAbsenceColor(avgFraværPct) }}>
              {fmtPct(Math.round(avgFraværPct * 10) / 10)}
            </p>
          </div>
          <div className="bg-secondary rounded-xl p-3">
            <p className="text-[13px] text-muted-foreground mb-1">Starter senere</p>
            <KosFriSplit kos={pendingKos} fri={pendingFri} total={pendingCount} />
          </div>
          <div className="bg-secondary rounded-xl p-3">
            <p className="text-[13px] text-muted-foreground mb-1">Orlov</p>
            <KosFriSplit kos={orlovKos} fri={orlovFri} total={orlovTotal} />
          </div>
        </div>

        <SortableTable students={activeStudentList.concat(pendingList)} onImpersonate={onImpersonate} />
      </CardContent>
    </Card>
  );
}

function eduBarColor(pct) {
  if (pct >= 66) return "#22C55E";
  if (pct >= 33) return "#EAB308";
  return "#FF2A6D";
}

// Produktive timer-kategorier (tæller med i nævner)
const PRODUKTIVE_KATEGORIER = [
  { label: "Praktiske timer",  keys: ["Arbejdstid (book kunder online og kvikbook)", "BUFFER", "Frokost", "Pause"], fallback: "arbejdstid" },
  { label: "Teori",       keys: ["Teori"],        fallback: null },
  { label: "Grundmodul",  keys: ["Grundmodul"],   fallback: null },
  { label: "PRÆFEKT",     keys: ["PRÆFEKT"],      fallback: null },
  { label: "Eksamen",     keys: ["Eksamen"],      fallback: null },
];

function GuestDetailCard({ student: s }) {
  const th = s.type_hours || {};

  const getHours = (keys, fallback) => {
    const fromTypes = keys.reduce((sum, k) => sum + (th[k] || 0), 0);
    if (fromTypes > 0) return fromTypes;
    if (fallback && s[fallback] != null) return s[fallback];
    return 0;
  };

  const kpiFravær = s.kpi_fravær_pct ?? s.fravær_pct;
  const absColor = getAbsenceColor(kpiFravær);
  const normeret = s.normeret_timer || 0;
  const gennemfort = s.gennemfort_timer || 0;
  const timerTilbage = Math.max(0, normeret - gennemfort);
  const eduPct = normeret > 0 ? Math.min(100, (gennemfort / normeret) * 100) : 0;
  const barColor = eduBarColor(eduPct);

  // Produktive timer (total nævner)
  const produktivTotal = s.total || PRODUKTIVE_KATEGORIER.reduce((sum, cat) => sum + getHours(cat.keys, cat.fallback), 0);

  // Fravær-grupper
  const ulovligtFravær = s.ulovligt_fravær ?? (s.udeblivelse || 0) + (s.buffer2 || 0);
  const sygdomTimer = s.sygdom || 0;
  const ferieTimer = s.ferie || 0; // inkl. fri

  return (
    <Card className="bg-card border-border shadow-none">
      <CardContent className="pt-6 pb-6">
        <div className="space-y-1">

          {/* Produktive timer */}
          {PRODUKTIVE_KATEGORIER.map(cat => {
            const h = getHours(cat.keys, cat.fallback);
            if (!h) return null;
            return (
              <div key={cat.label} className="flex items-center justify-between py-1.5 border-b border-border">
                <span className="text-sm text-foreground/70">{cat.label}</span>
                <span className="text-base font-bold font-kpi text-foreground/70">{fmtHours(h)}</span>
              </div>
            );
          })}

          {/* Sygdom */}
          {sygdomTimer > 0 && (
            <div className="flex items-center justify-between py-1.5 border-b border-border">
              <span className="text-sm text-orange-500">Sygdom</span>
              <span className="text-base font-bold font-kpi text-orange-500">{fmtHours(sygdomTimer)}</span>
            </div>
          )}

          {/* Ferie / Fri */}
          {ferieTimer > 0 && (
            <div className="flex items-center justify-between py-1.5 border-b border-border">
              <span className="text-sm text-blue-400">Ferie / Fri</span>
              <span className="text-base font-bold font-kpi text-blue-400">{fmtHours(ferieTimer)}</span>
            </div>
          )}

          {/* Ulovligt fravær */}
          {ulovligtFravær > 0 && (
            <div className="flex items-center justify-between py-1.5 border-b border-border">
              <span className="text-sm text-yellow-500">Fravær / Udeblevet</span>
              <span className="text-base font-bold font-kpi text-yellow-500">{fmtHours(ulovligtFravær)}</span>
            </div>
          )}

          {/* Fraværsprocent */}
          <div className="flex items-start justify-between py-2 mt-1">
            <span className="text-sm text-foreground">Fraværsprocent</span>
            <div className="text-right">
              <span className="text-xl font-bold font-kpi" style={{ color: absColor }}>{fmtPct(kpiFravær)}</span>
              {s.fravær_pct != null && s.fravær_pct !== kpiFravær && (
                <p className="text-[13px] text-muted-foreground mt-0.5">{fmtPct(s.fravær_pct)} inkl. ferie/fri</p>
              )}
            </div>
          </div>

          {normeret > 0 && (
            <>
              <div className="border-t border-border my-3" />
              {gennemfort >= normeret ? (
                <div className="rounded-xl bg-green-500/10 border border-green-500/30 p-4 text-center">
                  <div className="text-2xl mb-2">🎉⭐🎓</div>
                  <p className="text-sm font-bold text-green-600 dark:text-green-400 mb-1">
                    Tillykke! Du har gennemført alle dine timer!
                  </p>
                  <p className="text-[13px] text-muted-foreground mb-3">
                    Du er nu klar til eksamen — held og lykke! 🌟
                  </p>
                  <div className="relative w-full h-4 bg-secondary rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full w-full"
                      style={{ backgroundColor: "#22C55E", boxShadow: "0 0 12px 3px #22C55E" }}
                    />
                  </div>
                  <p className="text-[13px] text-green-600 dark:text-green-400 mt-1.5 font-medium">
                    {fmtHours(gennemfort)} / {fmtHours(normeret)} — 100% ✓
                  </p>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Fremgang i uddannelsen</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold font-kpi" style={{ color: barColor }}>
                        {Math.round(eduPct)}%
                      </span>
                      <span className="text-[13px] text-muted-foreground">
                        ({fmtHours(gennemfort)} / {fmtHours(normeret)})
                      </span>
                    </div>
                  </div>
                  <div className="relative w-full h-4 bg-secondary rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${eduPct}%`, backgroundColor: barColor }}
                    />
                  </div>
                  <p className="text-[13px] text-muted-foreground mt-1.5">
                    {fmtHours(timerTilbage)} tilbage
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function formatTimestamp(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("da-DK", {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return null; }
}

export const TimerView = ({ data, userCity, isAdmin, isGuest: isGuestProp, token, impersonatedStudent, setImpersonatedStudent, canImpersonate }) => {
  // By-filter (Alle · København · ... ) — kun relevant for admin (member er låst
  // til egen by). "Alle" som default.
  const [byFilter, setByFilter] = useState("Alle");

  if (!data) return null;

  const { by_city, total_count, active_count, overall_fravær_pct, last_updated, pending_by_city, next_exam_by_city, orlov_by_city,
    starter_senere_total, klar_eksamen_total, orlov_total, klar_eksamen_by_city } = data;
  const displayActiveCount = active_count ?? total_count; // bagud-kompatibelt

  const harData = (c) => (by_city[c] && by_city[c].length > 0) || (pending_by_city && pending_by_city[c] && pending_by_city[c].length > 0);
  // Byer der overhovedet har data (til by-menuens valg).
  const byerMedData = CITIES.filter(harData);
  // Anvendt by-valg: member låst til egen by; admin styrer via byFilter.
  const valgtBy = isAdmin ? byFilter : (userCity || "Alle");

  const citiesToShow = (
    valgtBy !== "Alle"
      ? [valgtBy].filter(harData)
      : (isAdmin || !userCity ? byerMedData : [userCity].filter(harData))
  );

  if (citiesToShow.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground gap-3">
        <Clock className="w-10 h-10 opacity-30" />
        <p className="text-sm">Ingen timerdata tilgængeligt</p>
      </div>
    );
  }

  // Overall stats — følger by-valget (citiesToShow).
  const allStudents = citiesToShow.flatMap((c) => by_city[c] || []);

  const highAbsence = allStudents.filter((s) => s.fravær_pct >= 10).length;
  const kosCount = allStudents.filter((s) => s.uddannelse_type === "kos").length;
  const friCount = allStudents.filter((s) => s.uddannelse_type === "fri").length;

  const isGuest = isGuestProp !== undefined ? isGuestProp : (!isAdmin && total_count <= 1);

  const allowImpersonate = canImpersonate !== undefined ? canImpersonate : isAdmin;
  const handleImpersonate = allowImpersonate && setImpersonatedStudent ? async (studentName) => {
    try {
      const res = await fetch(`${API}/auth/impersonate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ student_name: studentName }),
      });
      if (!res.ok) throw new Error(await res.text());
      const { token: impToken } = await res.json();
      setImpersonatedStudent({ name: studentName, token: impToken });
    } catch (err) {
      console.error("Impersonate fejl:", err);
      alert(`Kunne ikke åbne elev-visning: ${err.message}`);
    }
  } : null;

  // By-menu-valg: Alle + byer med data (kun for admin; member er låst til egen by).
  const byMenuTabs = [{ key: "Alle", label: "Alle" }, ...byerMedData.map((c) => ({ key: c, label: displayCity(c) }))];

  return (
    <div className="space-y-6" data-testid="timer-view">
      {/* By-undermenu (Alle · København · Kolding · …) — samme SubTabs-skabelon. */}
      {isAdmin && byerMedData.length > 1 && (
        <SubTabs tabs={byMenuTabs} value={byFilter} onChange={setByFilter} />
      )}

      {/* Summary row */}
      {isGuest ? (
        /* Personal summary for single student */
        allStudents.length > 0 && (() => {
          const s = allStudents[0];
          const absColor = getAbsenceColor(s.fravær_pct);
          return (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Card className="bg-card border-border shadow-none">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-[13px] text-muted-foreground">Gennemførte timer</p>
                      <p className="text-2xl font-bold text-foreground font-kpi">{fmtHours(s.gennemfort_timer || s.arbejdstid)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-card border-border shadow-none">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center">
                      <AlertTriangle className="w-5 h-5 text-orange-400" />
                    </div>
                    <div>
                      <p className="text-[13px] text-muted-foreground">Sygdom</p>
                      <p className="text-2xl font-bold font-kpi" style={{ color: s.sygdom > 0 ? "#F97316" : "#6b7280" }}>{fmtHours(s.sygdom)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-card border-border shadow-none">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-[13px] text-muted-foreground">Ferie</p>
                      <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 font-kpi">{fmtHours(s.ferie)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-card border-border shadow-none">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${absColor}18` }}>
                      <AlertTriangle className="w-5 h-5" style={{ color: absColor }} />
                    </div>
                    <div>
                      <p className="text-[13px] text-muted-foreground">Fraværspct.</p>
                      <p className="text-2xl font-bold font-kpi" style={{ color: absColor }}>{fmtPct(s.kpi_fravær_pct ?? s.fravær_pct)}</p>
                      <p className="text-[13px] text-muted-foreground mt-0.5">sygdom + udeblevet</p>
                      {s.fravær_pct != null && s.kpi_fravær_pct !== s.fravær_pct && (
                        <p className="text-[13px] text-muted-foreground">{fmtPct(s.fravær_pct)} inkl. ferie/fri</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          );
        })()
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Card className="bg-card border-border shadow-none">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 shrink-0 rounded-full bg-[#FF2A6D]/10 flex items-center justify-center">
                      <Users className="w-5 h-5 text-[#FF2A6D]" />
                    </div>
                    <div>
                      <p className="text-[13px] text-muted-foreground">Aktive elever</p>
                      <p className="text-2xl font-bold text-foreground font-kpi">{displayActiveCount}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-card border-border shadow-none">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 shrink-0 rounded-full bg-purple-500/10 flex items-center justify-center">
                      <Users className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <p className="text-[13px] text-muted-foreground">Kosmetolog elever</p>
                      <p className="text-2xl font-bold text-foreground font-kpi">{kosCount}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-card border-border shadow-none">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 shrink-0 rounded-full bg-blue-500/10 flex items-center justify-center">
                      <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="text-[13px] text-muted-foreground">Frisør elever</p>
                      <p className="text-2xl font-bold text-foreground font-kpi">{friCount}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              {overall_fravær_pct != null && (() => {
                const absColor = getAbsenceColor(overall_fravær_pct);
                return (
                  <Card className="bg-card border-border shadow-none">
                    <CardContent className="pt-5 pb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 shrink-0 rounded-full flex items-center justify-center" style={{ background: `${absColor}18` }}>
                          <AlertTriangle className="w-5 h-5" style={{ color: absColor }} />
                        </div>
                        <div>
                          <p className="text-[13px] text-muted-foreground">Samlet fraværspct.</p>
                          <p className="text-2xl font-bold font-kpi" style={{ color: absColor }}>{fmtPct(overall_fravær_pct)}</p>
                          <p className="text-[13px] text-muted-foreground mt-0.5">alle afdelinger</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })()}
        </div>
      )}

      {/* Opfølgning #33 (#4): kategori-tællere så superadmin har det fulde billede.
          'Aktive elever' er renset — pending/orlov/klar-til-eksamen vises separat. */}
      {!isGuest && (() => {
        // #47.8: kos/frisør-opdeling i ALLE opdelte totaler (samme stil som by-bokse).
        // Aktive: fra allStudents. Starter senere/Orlov/Klar: summér by-objekternes splits.
        const sumSplit = (obj) => Object.values(obj || {}).reduce(
          (a, v) => ({ kos: a.kos + (v.kos || 0), fri: a.fri + (v.fri || 0) }), { kos: 0, fri: 0 });
        const pendingSplit = (() => {
          let kos = 0, fri = 0;
          Object.values(pending_by_city || {}).forEach(list => (list || []).forEach(s => {
            if (s.uddannelse_type === "kos") kos++; else if (s.uddannelse_type === "fri") fri++;
          }));
          return { kos, fri };
        })();
        const orlovSplit = sumSplit(orlov_by_city);
        const klarSplit = sumSplit(klar_eksamen_by_city);
        const cards = [
          { label: "Aktive elever", value: displayActiveCount, color: "#FF2A6D", kos: kosCount, fri: friCount },
          { label: "Starter senere", value: starter_senere_total ?? 0, color: "#8B5CF6", kos: pendingSplit.kos, fri: pendingSplit.fri },
          { label: "Orlov", value: orlov_total ?? 0, color: "#F59E0B", kos: orlovSplit.kos, fri: orlovSplit.fri },
          { label: "Klar til eksamen", value: klar_eksamen_total ?? 0, color: "#10B981", kos: klarSplit.kos, fri: klarSplit.fri },
        ];
        return (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {cards.map((k) => (
              <div key={k.label} className="rounded-xl border border-border bg-card px-4 py-3">
                <div className="flex items-center justify-between">
                  <span className="text-[13px] text-muted-foreground">{k.label}</span>
                  <span className="text-xl font-bold font-kpi" style={{ color: k.color }}>{k.value}</span>
                </div>
                {(k.kos > 0 || k.fri > 0) && (
                  <p className="text-[11px] mt-0.5 text-right">
                    <span style={{ color: "#EC4899" }}>{k.kos} kos</span>
                    <span className="text-muted-foreground"> · </span>
                    <span style={{ color: "#3B82F6" }}>{k.fri} frisør</span>
                  </p>
                )}
              </div>
            ))}
          </div>
        );
      })()}

      {/* Per-city tables / personal detail */}
      <div className="space-y-4">
        {isGuest && allStudents.length > 0 ? (
          <GuestDetailCard student={allStudents[0]} />
        ) : (
          citiesToShow.map((city) => (
            <SummaryCard
              key={city}
              city={city}
              students={by_city[city] || []}
              onImpersonate={handleImpersonate}
              pendingStudents={pending_by_city ? pending_by_city[city] : null}
              nextExamInfo={next_exam_by_city ? next_exam_by_city[city] : null}
              orlovInfo={orlov_by_city ? orlov_by_city[city] : 0}
            />
          ))
        )}
      </div>

      {last_updated && (
        <p className="text-[12px] text-muted-foreground text-center pt-2">
          Sidst opdateret: {formatTimestamp(last_updated)}
        </p>
      )}
    </div>
  );
};
